import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Container,
  Divider,
  useMediaQuery,
  useTheme,
  Avatar,
  Fab,
  Zoom
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import ChatIcon from '@mui/icons-material/Chat';
import BusinessIcon from '@mui/icons-material/Business';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useGuide } from '../../context/GuideContext';

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { startGuide } = useGuide();

  const menuItems = [
    { text: '홈', icon: <HomeIcon />, path: '/' },
    { text: 'AI 질의응답', icon: <ChatIcon />, path: '/chat' },
    { text: '적용 사례', icon: <BusinessIcon />, path: '/cases' },
  ];

  // 스크롤 이벤트 처리
  React.useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleStartGuide = () => {
    // 현재 경로에 따라 다른 가이드 시작
    if (location.pathname === '/') {
      startGuide('home');
    } else if (location.pathname === '/chat') {
      startGuide('chat');
    } else if (location.pathname === '/cases') {
      startGuide('cases');
    }
  };

  const drawer = (
    <Box
      sx={{ 
        width: 280,
        background: 'linear-gradient(135deg, #263238 0%, #37474F 100%)',
        color: 'white',
        height: '100%'
      }}
      role="presentation"
      onClick={toggleDrawer(false)}
      onKeyDown={toggleDrawer(false)}
    >
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Avatar
          sx={{
            width: 60,
            height: 60,
            margin: '0 auto',
            mb: 2,
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 30 }} />
        </Avatar>
        <Typography variant="h6" component="div" sx={{ 
          fontWeight: 700,
          textShadow: '0 2px 4px rgba(0,0,0,0.3)',
          color: 'white'
        }}>
          AI 기반 질의응답 시스템
        </Typography>
        <Typography variant="body2" sx={{ 
          opacity: 0.95, 
          mt: 1,
          textShadow: '0 1px 2px rgba(0,0,0,0.2)',
          color: 'white',
          fontWeight: 500
        }}>
          스마트한 정보 검색의 새로운 경험
        </Typography>
      </Box>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
      <List sx={{ px: 2 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              selected={isActive(item.path)}
              onClick={() => navigate(item.path)}
              sx={{
                borderRadius: '8px',
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  }
                },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                sx={{ 
                  '& .MuiListItemText-primary': { 
                    fontWeight: isActive(item.path) ? 700 : 500,
                    textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    color: 'white'
                  } 
                }} 
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)', mx: 2 }} />
      <List sx={{ px: 2 }}>
        <ListItem disablePadding sx={{ mt: 1 }}>
          <ListItemButton 
            onClick={() => navigate('/admin')} 
            data-guide="admin-button"
            sx={{
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
              <AdminPanelSettingsIcon />
            </ListItemIcon>
            <ListItemText 
              primary="관리자 메뉴" 
              sx={{ 
                '& .MuiListItemText-primary': { 
                  fontWeight: 500,
                  textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                  color: 'white'
                } 
              }} 
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="static" elevation={0}>
        <Toolbar sx={{ py: 1 }}>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              sx={{ mr: 2 }}
              onClick={toggleDrawer(true)}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                mr: 2,
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <AutoAwesomeIcon />
            </Avatar>
            <Typography
              variant="h6"
              sx={{
                mr: 2,
                display: { xs: 'none', md: 'flex' },
                fontWeight: 700,
                color: 'inherit',
                textDecoration: 'none',
                textShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              AI 기반 질의응답 시스템
            </Typography>
          </Box>
          
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.text}
                  color="inherit"
                  onClick={() => navigate(item.path)}
                  startIcon={item.icon}
                  sx={{
                    fontWeight: isActive(item.path) ? 'bold' : 'normal',
                    borderRadius: '6px',
                    px: 2.5,
                    py: 0.8,
                    backgroundColor: isActive(item.path) ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      transform: 'translateY(-1px)',
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {item.text}
                </Button>
              ))}
              <Button
                color="inherit"
                onClick={() => navigate('/admin')}
                data-guide="admin-button"
                startIcon={<AdminPanelSettingsIcon />}
                sx={{
                  borderRadius: '6px',
                  px: 2.5,
                  py: 0.8,
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    transform: 'translateY(-1px)',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                관리자
              </Button>
              <IconButton
                color="inherit"
                onClick={handleStartGuide}
                sx={{ 
                  ml: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  color: 'white',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.25)',
                    transform: 'scale(1.05)',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  transition: 'all 0.2s ease'
                }}
                aria-label="가이드 보기"
              >
                <HelpOutlineIcon />
              </IconButton>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            background: 'linear-gradient(135deg, #263238 0%, #37474F 100%)',
          }
        }}
      >
        {drawer}
      </Drawer>
      
      <Box sx={{ 
        minHeight: 'calc(100vh - 140px)',
        background: 'transparent'
      }}>
        <Container component="main" sx={{ pt: 4, pb: 6 }}>
          <Outlet />
        </Container>
      </Box>
      
      {/* 스크롤 탑 버튼 */}
      <Fab
        color="primary"
        size="medium"
        aria-label="scroll to top"
        onClick={scrollToTop}
        sx={{
          position: 'fixed',
          bottom: 20,
          right: 20,
          opacity: showScrollTop ? 1 : 0,
          transform: showScrollTop ? 'scale(1)' : 'scale(0)',
          transition: 'all 0.3s ease',
          background: '#1565C0',
          '&:hover': {
            background: '#0D47A1',
            transform: showScrollTop ? 'scale(1.1)' : 'scale(0)',
            boxShadow: '0 6px 20px rgba(21, 101, 192, 0.4)',
          },
        }}
      >
        <KeyboardArrowUpIcon />
      </Fab>
      
      <Box
        component="footer"
        sx={{
          py: 4,
          px: 2,
          mt: 'auto',
          background: 'linear-gradient(135deg, #263238 0%, #37474F 100%)',
          color: 'white',
          textAlign: 'center',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ mb: 3 }}>
            <Avatar
              sx={{
                width: 50,
                height: 50,
                margin: '0 auto',
                mb: 2,
                background: 'linear-gradient(45deg, #1565C0 30%, #37474F 90%)',
                border: '2px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 25, color: 'white' }} />
            </Avatar>
            <Typography variant="h6" component="div" sx={{ 
              fontWeight: 600, 
              mb: 1, 
              color: 'white',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
            }}>
              {process.env.REACT_APP_TITLE || 'AI 기반 질의응답 시스템'}
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'rgba(255, 255, 255, 0.9)', 
              maxWidth: 600, 
              margin: '0 auto',
              textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
            }}>
              최신 AI 기술로 정확하고 신뢰할 수 있는 답변을 제공하는 차세대 질의응답 플랫폼
            </Typography>
          </Box>
          
          <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.3)', mb: 3 }} />
          
          <Typography variant="body2" sx={{ 
            color: 'rgba(255, 255, 255, 0.8)',
            textShadow: '1px 1px 2px rgba(0,0,0,0.2)'
          }}>
            © {new Date().getFullYear()} AI 기반 질의응답 시스템
          </Typography>
        </Container>
      </Box>
    </>
  );
};

export default MainLayout; 